import { imageVariantLink } from '@/common/media';
import type { Person } from '@/store/persons/person.dto';
import { SubscriptionTier } from '@/store/subscriptions/subscriptions.types';
import type { Subscription } from '@/store/subscriptions/subscriptions.types';
import type { Vessel } from '@/store/vessels/vessel.dto';
import { UserRole } from '@/utils/roles';

import { isPaidPlan, isUpdatable } from './subscription-display';

/**
 * The seat model behind the vessel detail view: which seats a subscription pays for and who
 * sits in them. Mirrors `subscription.seats.ts` in the subscriptions portal, minus
 * everything about assigning - this view is read-only.
 *
 * A subscription buys two separate pools - `seats`, the billable management seats, and
 * `crew_seats`, a cheaper add-on. Each pool maps to one seat type per tier, and a person
 * occupies at most one of them through their role's `seat_type`.
 */

/**
 * Which subscription seat a person occupies on a service, set through `PATCH /roles/seat`.
 * Only valid against the service's current tier - `harbor_seat` belongs to harbor,
 * `command_crew` to command, and so on.
 */
export enum SeatType {
  HARBOR_SEAT = 'harbor_seat',
  NAVIGATE_SEAT = 'navigate_seat',
  NAVIGATE_CREW = 'navigate_crew',
  COMMAND_SEAT = 'command_seat',
  COMMAND_CREW = 'command_crew',
}

export type SeatPool = 'management' | 'crew';

export const SEAT_POOLS: readonly SeatPool[] = ['management', 'crew'];

export const SEAT_POOL_LABEL: Record<SeatPool, string> = {
  management: 'Management',
  crew: 'Crew',
};

/**
 * Mirrors `seatCapacity` in the People service's `internal/app/people/helpers.go`: a seat
 * type only exists for the tier it names, and the server answers `ErrSeatTierMismatch` for
 * any other pairing. `harbor` buys no crew pool at all.
 */
const SEAT_TYPE_BY_TIER: Record<SubscriptionTier, Record<SeatPool, SeatType | null>> = {
  [SubscriptionTier.HARBOR]: { management: SeatType.HARBOR_SEAT, crew: null },
  [SubscriptionTier.NAVIGATE]: { management: SeatType.NAVIGATE_SEAT, crew: SeatType.NAVIGATE_CREW },
  [SubscriptionTier.COMMAND]: { management: SeatType.COMMAND_SEAT, crew: SeatType.COMMAND_CREW },
};

export const seatTypeFor = (tier: SubscriptionTier | null, pool: SeatPool): SeatType | null =>
  tier ? SEAT_TYPE_BY_TIER[tier][pool] : null;

/**
 * How many seats the pool holds. Zero unless the subscription is one the server treats as
 * live - a dead row still carries seat counts, but nothing behind it was ever provisioned.
 */
export const seatCapacity = (subscription: Subscription | null, pool: SeatPool): number => {
  if (!isUpdatable(subscription) || !seatTypeFor(subscription.tier, pool)) return 0;
  return pool === 'management' ? subscription.seats : subscription.crewSeats;
};

/**
 * How many of the pool's seats are spoken for by an owner who has not come aboard yet - one,
 * or none.
 *
 * A vessel with no owner still has a reserved management seat, and the captain paying for it
 * said at checkout whether it is theirs or the owner's to come. Said the owner's, the seat is
 * nobody's until they join: `seatForPayingCaptain` (the People service's
 * `internal/app/payments/captain.go`) counts it as taken while no person holds it. So it is
 * paid for, empty, and not a seat anyone is waiting to be given.
 *
 * Only ever the management pool, and only on a paid plan: harbor's seat is the free default
 * everybody already holds, and nothing is reserved out of it.
 */
export const seatsHeldForOwner = (
  vessel: Pick<Vessel, 'owner_id'>,
  subscription: Subscription | null,
  pool: SeatPool,
): number =>
  pool === 'management' &&
  !vessel.owner_id &&
  isPaidPlan(subscription) &&
  subscription.captainWillAddOwner
    ? 1
    : 0;

/**
 * A person as a seat shows them: a face, a name to fall back on, the seat they hold, and the
 * role that decides where in the row they sit.
 */
export type SeatPerson = {
  personId: string;
  firstName: string;
  lastName: string;
  avatar: string;
  role: string;
  seatType: string | null;
};

/** Everyone with a role on this vessel, flattened to what the seat rows need. */
export const seatPeopleOf = (people: Person[], serviceId: string): SeatPerson[] =>
  people.flatMap((person) => {
    const membership = person.services?.find((role) => role.service_id === serviceId);
    if (!membership) return [];
    return [
      {
        personId: person.id,
        firstName: person.person_info.first_name,
        lastName: person.person_info.last_name,
        avatar: imageVariantLink(person.avatar, 30) ?? '',
        role: membership.role,
        seatType: membership.seat_type ?? null,
      },
    ];
  });

export const displayName = ({ firstName, lastName }: SeatPerson): string =>
  [firstName, lastName].filter(Boolean).join(' ') || 'Unnamed';

/**
 * The owner leads the row: theirs is the seat that came with the vessel, so it reads as the
 * anchor the rest of the pool hangs off. The co-owners follow, since each of them keeps a
 * management seat too. Everyone else keeps the order the API returned them in - `sort` is stable.
 */
const ownerRank = (role: string): number =>
  role === UserRole.OWNER ? 2 : role === UserRole.CO_OWNER ? 1 : 0;

export const byOwnerFirst = (a: SeatPerson, b: SeatPerson): number =>
  ownerRank(b.role) - ownerRank(a.role);
